import { Box, Stack, styled, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { primaryColor } from '../../utils/theme'

// --- Styled Component Definitions ---

// 1. Chart Bubble Component
// This component accepts all dynamic properties (size, color, value, position) via props.
interface BubbleProps {
  bubbleColor: string;
  bubbleSize: number;
  zIndex: number;
}

export const ChartBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'bubbleColor' && prop !== 'bubbleSize' && prop !== 'zIndex',
})<BubbleProps>(({ theme, bubbleColor, bubbleSize, zIndex }) => ({
  position: 'absolute',
  width: bubbleSize,
  height: bubbleSize,

  // Gradient Background for depth
  background: `linear-gradient(135deg, ${bubbleColor} 30%, ${alpha(bubbleColor, 0.85)} 90%)`,

  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  fontSize: bubbleSize * 0.25,
  fontWeight: 600,
  zIndex: zIndex,

  // The "Cutout" White Border
  border: '4px solid white',
  boxSizing: 'content-box',

  // The "Halo" and "Drop Shadow" Effects
  boxShadow: `
    0 0 0 4px ${alpha(bubbleColor, 0.2)},  /* Outer Colored Ring */
    0 8px 16px ${alpha(bubbleColor, 0.3)}   /* Drop Shadow */
  `,

  // Hover Effects
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  cursor: 'default',
  '&:hover': {
    transform: 'scale(1.05)',
    zIndex: 10,
    boxShadow: `
      0 0 0 6px ${alpha(bubbleColor, 0.3)}, 
      0 12px 24px ${alpha(bubbleColor, 0.4)}
    `,
  },
}));

// 2. Chart Container
export const ChartContainer = styled(Box)({
  position: 'relative',
  height: 180,
  display: 'flex',
  justifyContent: 'center',
  marginBottom: 16, // Equivalent to mb={2}
});

// 3. Legend Dot Component
interface DotProps {
  dotColor: string;
}

export const LegendDot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'dotColor',
})<DotProps>(({ theme, dotColor }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  backgroundColor: dotColor,
  boxShadow: `0 0 0 2px ${alpha(dotColor, 0.2)}`,
}));

// 4. Legend Container
export const LegendStack = styled(Stack)({
  flexWrap: 'wrap',
  justifyContent: 'center',
  marginTop: 16, // Equivalent to mt={2}
});

// 5. Card Title
export const ChartTitle = styled(Typography)({
  fontWeight: 500,
  marginBottom: 8, // Equivalent to mb={1}
  color: 'text.secondary', // Use theme color for text
});

interface StatusTagProps {
  status: 'assigned' | 'accepted' | 'declined' | 'cancelled' | 'facility_approval_pending' | 'facility_reject' | 'worker_cancelled' | 'completed' | string;
}

export const StatusTag = styled(Box)<StatusTagProps>(({ theme, status }) => {
  let bgColor: string;
  let color: string;

  switch (status) {
    case 'completed':
    case 'Active':
    case 'Completed':
    case 'Partially Completed':
      bgColor = alpha(primaryColor, 0.15);
      color = primaryColor;
      break;
    case 'assigned':
    case 'Upcoming':
      bgColor = alpha(theme.palette.link.main, 0.15);
      color = theme.palette.link.main;
      break;
    case 'pending':
    case 'Clocked In (Late)':
      bgColor = alpha(theme.palette.warning.light, 0.5);
      color = theme.palette.warning.dark;
      break;
    case 'partially_approval_pending':
    case 'facility_approval_pending':
      // Brownish color
      bgColor = alpha("#8D6E63", 0.15);
      color = "#8D6E63";
      break;
    case 'declined':
    case 'cancelled':
    case 'facility_reject':
    case 'worker_cancelled':
    case 'Absent':
    case 'Rejected':
    case 'Partially Rejected':
    case 'Cancelled':
      bgColor = alpha(theme.palette.error.main, 0.15);
      color = theme.palette.error.main;
      break;
    default:
      bgColor = theme.palette.grey[100];
      color = theme.palette.grey[600];
  }

  return {
    backgroundColor: bgColor,
    color: color,
    fontWeight: 600,
    fontSize: '0.75rem',
    padding: theme.spacing(0.5, 1.5),
    borderRadius: '4px',
    textAlign: 'center',
    width: 'fit-content',
    // float: "none",
    // margin: "0 auto",
  };
});